import { useContext, useRef, useState, useEffect } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import { format } from 'libs/dates'
import {
	ActionsSelect,
	CardWrapper,
	CtaLink,
	DeleteConfirmModal,
	ConfirmModal,
	EmptyTable,
	FormatModal,
	Header,
	SortSelect
} from 'shared/components'
import { useModal, useFilter, useSearch, useTableSelect } from 'shared/hooks'
import { IOption } from 'interfaces/select.interface'
import { defaultConfig } from 'config/default'
import { routes } from 'config/routes'
import { downloadAs } from 'utils/downloadFile'
import { useReactToPrint } from 'react-to-print'
import QRCode from 'react-qr-code'
import { StoreSettingsContext } from 'store/StoreSettingsContext'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import { useFetchProducts } from './queries/useFetchProducts'
import { useDeleteProductMutation } from './mutations/useDeleteProductMutation'
import { useDeleteProductsMutation } from './mutations/useDeleteProductsMutation'
import { useSortingFilters } from './hooks/useSortingFilters'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useShopifyProductMutation } from './mutations/useShopifyProductMutation'
import { usePublishProductsMutation } from './mutations/usePublishProductsMutation'
import { useUnpublishProductsMutation } from './mutations/useUnpublishProductsMutation'
import { useUpdateProductStatusMutation } from './mutations/useUpdateProductStatusMutation'
import { useExportProductsMutation } from './mutations/useExportProductsMutation'
import { usePrintProductMutation } from './mutations/usePrintProductMutation'
import { CheckedProduct, ProductItem } from 'features/products/types/products.types'
import { usePrintProductsMutation } from './mutations/usePrintProductsMutation'
import { cn } from 'utils/style'
import { EllipsisText } from 'shared/atoms'
import { useReglaProductMutation } from './mutations/useReglaProductMutation'
import { useConfirmProductsMutation } from './mutations/useConfirmProductsMutation'
import { useUnconfirmProductsMutation } from './mutations/useUnconfirmProductsMutation'
import { FiltersSection } from './organisms/FiltersSection/FiltersSection'
import { TableSection } from './organisms/TableSection'
import { PrintProductsDialog } from './organisms/PrintDialog/PrintProductsDialog'

const withPrintQuantity = (product: ProductItem): CheckedProduct => ({
	...product,
	__print_quantity: Math.max(product.quantity - product.print_count, 1)
})

const ProductsPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { handleSearch } = useSearch({ name: 'search' })
	const { handleFilter } = useFilter()
	const [chosenProductId, setChosenProductId] = useState<number | null>(null)
	const [checkedProducts, setCheckedProducts] = useState<number[]>([])
	const [chosenExportFormat, setChosenExportFormat] = useState<number | string | null>(null)
	const [checkedProductsData, setCheckedProductsData] = useState<{
		nodes: ProductItem[]
	}>({
		nodes: []
	})
	const [printProducts, setPrintProducts] = useState<CheckedProduct[]>([])
	const [actionsFilter] = useActionFilters(!checkedProducts.length)
	const { products, isProductsLoading } = useFetchProducts()
	const { select } = useTableSelect({ nodes: products?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const clearSelection = () => select.fns.onRemoveAll()
	const storeCtx = useContext(StoreSettingsContext)
	const locale = storeCtx?.storeSettings?.country

	const [isPrinting, setIsPrinting] = useState<boolean>(false)
	const [printDate, setPrintDate] = useState<string>('')
	const printRef = useRef<any>()

	// We store the resolve Promise being used in `onBeforeGetContent` here
	const promiseResolveRef = useRef<any>(null)

	// We watch for the state to change here, and for the Promise resolve to be available
	useEffect(() => {
		if (isPrinting && promiseResolveRef.current) {
			// Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
			promiseResolveRef.current()
		}
	}, [isPrinting])

	const handlePrintComponent = useReactToPrint({
		content: () => printRef.current,
		onBeforeGetContent: () => {
			return new Promise((resolve) => {
				promiseResolveRef.current = resolve
				// Capture the print date once so every label in the batch shares it (issue #828)
				setPrintDate(format(new Date(), 'dd.MM.yyyy'))
				setIsPrinting(true)
			})
		},
		onAfterPrint: () => {
			// Reset the Promise resolve so we can print again
			promiseResolveRef.current = null
			setIsPrinting(false)
		}
	})

	const handleFormatChange = (data: IOption) => {
		data?.value?.toString() ? setChosenExportFormat(data.value) : setChosenExportFormat(null)
	}

	const {
		isOpen: isSingleDeleteModalOpen,
		openModal: openSingleDeleteModal,
		closeModal: closeSingleDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()
	const {
		isOpen: isPublishModalOpen,
		openModal: openPublishModal,
		closeModal: closePublishModal
	} = useModal()
	const {
		isOpen: isPrintModalOpen,
		openModal: openPrintModal,
		closeModal: closePrintModal
	} = useModal()
	const {
		isOpen: isBulkPrintModalOpen,
		openModal: openBulkPrintModal,
		closeModal: closeBulkPrintModal
	} = useModal()
	const {
		isOpen: isUnpublishModalOpen,
		openModal: openUnpublishModal,
		closeModal: closeUnpublishModal
	} = useModal()
	const {
		isOpen: isShopifyModalOpen,
		openModal: openShopifyModal,
		closeModal: closeShopifyModal
	} = useModal()
	const {
		isOpen: isReglaModalOpen,
		openModal: openReglaModal,
		closeModal: closeReglaModal
	} = useModal()
	const {
		isOpen: isExportModalOpen,
		openModal: openExportModal,
		closeModal: closeExportModal
	} = useModal()
	const {
		isOpen: isFormatModalOpen,
		openModal: openFormatModal,
		closeModal: closeFormatModal
	} = useModal()
	const {
		isOpen: isConfirmModalOpen,
		openModal: openConfirmModal,
		closeModal: closeConfirmModal
	} = useModal()
	const {
		isOpen: isUnconfirmModalOpen,
		openModal: openUnconfirmModal,
		closeModal: closeUnconfirmModal
	} = useModal()

	const { productDeleteMutation } = useDeleteProductMutation({
		onSettled: () => closeSingleDeleteModal()
	})

	const { productsDeleteMutation } = useDeleteProductsMutation({
		onSettled: () => {
			clearSelection()
			closeBulkDeleteModal()
		}
	})

	const handleDelete = (id: number) => {
		setChosenProductId(id)
		openSingleDeleteModal()
	}

	const handlePrint = (id: number) => {
		const product = products?.data?.results?.find((item) => item.id === id)
		if (!product) return

		setPrintProducts([withPrintQuantity(product)])
		openPrintModal()
	}

	const handleAction = (action: string) => {
		switch (action) {
			case 'delete':
				openBulkDeleteModal()
				break
			case 'publish':
				openPublishModal()
				break
			case 'print':
				setPrintProducts(checkedProductsData.nodes.map(withPrintQuantity))
				openBulkPrintModal()
				break
			case 'unpublish':
				openUnpublishModal()
				break
			case 'shopify':
				openShopifyModal()
				break
			case 'regla':
				openReglaModal()
				break
			case 'export':
				openExportModal()
				break
			case 'confirm':
				openConfirmModal()
				break
			case 'unconfirm':
				openUnconfirmModal()
				break
		}
	}

	const { shopifyProductMutation } = useShopifyProductMutation(() => {
		clearSelection()
		closeShopifyModal()
	})

	const { reglaProductMutation } = useReglaProductMutation(() => {
		clearSelection()
		closeReglaModal()
	})

	const { publishProductsMutation } = usePublishProductsMutation(() => {
		clearSelection()
		closePublishModal()
	})

	const { unpublishProductsMutation } = useUnpublishProductsMutation(() => {
		clearSelection()
		closeUnpublishModal()
	})

	const { exportProductsMutation, isExportProductsLoading } = useExportProductsMutation({
		onSuccess: (res: any) => {
			const format = defaultConfig.exportFormatOptions
				.find((format) => format.value === chosenExportFormat)
				?.label.toLowerCase()
			if (format) {
				downloadAs({ file: res.data, name: 'products-export', format })
			}
		},
		onSettled: () => {
			clearSelection()
			closeFormatModal()
		}
	})

	const { printProductMutation } = usePrintProductMutation({
		handlePrintComponent,
		onSettled: () => closePrintModal()
	})

	const { printProductsMutation } = usePrintProductsMutation({
		handlePrintComponent,
		onSettled: () => {
			clearSelection()
			closeBulkPrintModal()
		}
	})

	const { confirmProductsMutation } = useConfirmProductsMutation(() => {
		clearSelection()
		closeConfirmModal()
	})
	const { unconfirmProductsMutation } = useUnconfirmProductsMutation(() => {
		clearSelection()
		closeUnconfirmModal()
	})

	const handleExportModal = () => {
		closeExportModal()
		openFormatModal()
	}

	const sortingFilters = useSortingFilters()
	const { updateProductStatusMutation } = useUpdateProductStatusMutation()
	const getMenuOptions = useContextMenuOptions({
		handlePrint,
		handleDelete,
		updateProductStatusMutation
	})

	function onSelectChange(action: any, state: any) {
		const results = products?.data?.results ?? []
		const pageIds = results.map(({ id }) => id)

		setCheckedProducts(state.ids)
		setCheckedProductsData(({ nodes }) => ({
			nodes: [
				...nodes.filter(({ id }) => state.ids.includes(id) && !pageIds.includes(id)),
				...results.filter(({ id }) => state.ids.includes(id))
			]
		}))
	}

	const printOptions: IOption[] = [
		{
			value: 'version1',
			label: 'Label 28×41'
		},
		{
			value: 'version2',
			label: 'Label 46×66'
		}
	]

	// set printVersion
	useEffect(() => {
		const lsPrintVersion = localStorage.getItem('printVersion')
		if (lsPrintVersion) {
			setPrintVersion(printOptions.find((o) => o.value === lsPrintVersion)!)
		} else {
			setPrintVersion(printOptions[0])
		}
	}, [])

	const [printVersion, setPrintVersion] = useState(printOptions[0])

	const handlePrintOptionChange = (data: IOption) => {
		setPrintVersion(data)
		localStorage.setItem('printVersion', String(data.value))
	}

	const handlePrintQuantityChange = (index: number, quantity: number) => {
		setPrintProducts((items) =>
			items.map((product, i) =>
				i === index ? { ...product, __print_quantity: quantity } : product
			)
		)
	}

	const renderContent = () => {
		if (isProductsLoading) return <EmptyTable isLoading />
		if (!products?.data?.results?.length)
			return <EmptyTable text={searchParams.toString() ? t('No results') : t('No products yet')} />

		return (
			<TableSection
				products={products}
				isLoading={isProductsLoading}
				select={select}
				getMenuOptions={getMenuOptions}
			/>
		)
	}

	return (
		<>
			<Header
				title={t('Products')}
				actions={
					<>
						<SortSelect {...sortingFilters} onChange={handleFilter} />
						<CtaLink to={routes.productAdd} text={t('Add product')} compact />
					</>
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-72px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<FiltersSection
						actions={
							<ActionsSelect
								variant="filters"
								options={actionsFilter.options}
								onChange={handleAction}
							/>
						}
						handleSearch={handleSearch}
						handleFilter={handleFilter}
					/>
					{renderContent()}
				</CardWrapper>
			</div>
			{isSingleDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete a product')}
					isOpened={isSingleDeleteModalOpen}
					closeModal={closeSingleDeleteModal}
					confirm={() => productDeleteMutation(chosenProductId)}>
					<p>{t('You are about to delete a product. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete products')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={() => productsDeleteMutation(checkedProducts)}>
					<p>
						{t('You are about to delete {{ count }} products. Are you sure you want to proceed?', {
							count: checkedProducts.length
						})}
					</p>
				</DeleteConfirmModal>
			)}
			{isPublishModalOpen && (
				<ConfirmModal
					title={t('Publish products')}
					isOpened={isPublishModalOpen}
					closeModal={closePublishModal}
					confirm={() => publishProductsMutation(checkedProducts)}>
					<p>
						{t('You are about to publish {{ count }} products. Are you sure you want to proceed?', {
							count: checkedProducts.length
						})}
					</p>
				</ConfirmModal>
			)}
			{isPrintModalOpen && (
				<PrintProductsDialog
					title={t('Print a product')}
					products={printProducts}
					printVersion={printVersion}
					printOptions={printOptions}
					closeModal={closePrintModal}
					confirm={() =>
						printProductMutation({
							id: printProducts[0].id,
							quantity: printProducts[0].__print_quantity
						})
					}
					onQuantityChange={handlePrintQuantityChange}
					onVersionChange={handlePrintOptionChange}
				/>
			)}
			{isBulkPrintModalOpen && (
				<PrintProductsDialog
					title={t('Print products')}
					products={printProducts}
					printVersion={printVersion}
					printOptions={printOptions}
					closeModal={closeBulkPrintModal}
					confirm={() => printProductsMutation(printProducts)}
					onQuantityChange={handlePrintQuantityChange}
					onVersionChange={handlePrintOptionChange}
				/>
			)}
			{isUnpublishModalOpen && (
				<ConfirmModal
					title={t('Unpublish products')}
					isOpened={isUnpublishModalOpen}
					closeModal={closeUnpublishModal}
					confirm={() => unpublishProductsMutation(checkedProducts)}>
					<p>
						{t(
							'You are about to unpublish {{ count }} products. Are you sure you want to proceed?',
							{ count: checkedProducts.length }
						)}
					</p>
				</ConfirmModal>
			)}
			{isShopifyModalOpen && (
				<ConfirmModal
					title={t('Sync with shopify')}
					isOpened={isShopifyModalOpen}
					closeModal={closeShopifyModal}
					confirm={() => shopifyProductMutation(checkedProducts)}>
					<p>
						{t(
							'You are about to sync {{ count }} products with Shopify. Are you sure you want to proceed?',
							{ count: checkedProducts.length }
						)}
					</p>
				</ConfirmModal>
			)}
			{isReglaModalOpen && (
				<ConfirmModal
					title={t('Sync with Regla')}
					isOpened={isReglaModalOpen}
					closeModal={closeReglaModal}
					confirm={() => reglaProductMutation(checkedProducts)}>
					<p>
						{t(
							'You are about to sync {{ count }} products with Regla. Are you sure you want to proceed?',
							{ count: checkedProducts.length }
						)}
					</p>
				</ConfirmModal>
			)}
			{isExportModalOpen && (
				<ConfirmModal
					title={t('Export products')}
					isOpened={isExportModalOpen}
					closeModal={closeExportModal}
					confirm={handleExportModal}>
					<p>
						{t('You are about to export {{ count }} products. Are you sure you want to proceed?', {
							count: checkedProducts.length
						})}
					</p>
				</ConfirmModal>
			)}
			{isFormatModalOpen && (
				<FormatModal
					disableButton={chosenExportFormat === null}
					isOpened={isFormatModalOpen}
					closeModal={closeFormatModal}
					confirm={() =>
						exportProductsMutation({ ids: checkedProducts, format: chosenExportFormat })
					}
					isLoading={isExportProductsLoading}
					onChange={handleFormatChange}
				/>
			)}
			{isConfirmModalOpen && (
				<ConfirmModal
					title={t('Confirm products')}
					isOpened={isConfirmModalOpen}
					closeModal={closeConfirmModal}
					confirm={() => confirmProductsMutation(checkedProducts)}>
					<p>
						{t('You are about to confirm {{ count }} products. Are you sure you want to proceed?', {
							count: checkedProducts.length
						})}
					</p>
				</ConfirmModal>
			)}
			{isUnconfirmModalOpen && (
				<ConfirmModal
					title={t('Unconfirm products')}
					isOpened={isUnconfirmModalOpen}
					closeModal={closeUnconfirmModal}
					confirm={() => unconfirmProductsMutation(checkedProducts)}>
					<p>
						{t(
							'You are about to unconfirm {{ count }} products. Are you sure you want to proceed?',
							{
								count: checkedProducts.length
							}
						)}
					</p>
				</ConfirmModal>
			)}
			{isPrinting && (
				<div ref={printRef}>
					{printProducts.length > 0 &&
						printProducts.map((product) => {
							return (
								<>
									{Array(product.__print_quantity)
										.fill(true)
										.map((_, i) => (
											<div
												key={`${product.id}-${i}`}
												className={cn(
													'print-container flex break-before-page flex-row items-stretch justify-center text-xs',
													printVersion.value === 'version1' ? 'h-[82px] w-[142px]' : 'w-[160px]'
												)}>
												<span className="flex shrink-0 rotate-180 items-center justify-center pr-[2px] text-[8px] leading-none [writing-mode:vertical-rl]">
													{printDate}
												</span>
												<div className="flex min-w-0 flex-1 flex-col items-center justify-center">
													<p className="text-xs leading-3">
														<strong style={{ fontSize: '12px' }}>
															{new Intl.NumberFormat(locale, {
																style: 'currency',
																currency: product.price.currency,
																currencyDisplay: 'symbol',
																useGrouping: true
																// minimumFractionDigits: the default for currency formatting is the number of minor unit digits provided by the ISO 4217 currency code list
															}).format(+product.price.amount)}
														</strong>
													</p>

													{/* TODO: handle those versions better */}
													{printVersion.value === 'version1' ? (
														<EllipsisText textCenter>
															<span className="px-[5px] text-xs leading-3">{product.name}</span>
														</EllipsisText>
													) : (
														<>
															<span className="max-w-[160px] break-normal px-[5px] text-center text-xs font-bold leading-3">
																{product.name}
															</span>

															<div className="text-center">
																<span>{product.category?.name}</span>
															</div>
														</>
													)}

													{!product.supplier_barcode ? (
														<QRCode
															size={40}
															style={{
																height: 'auto',
																maxWidth: '100%',
																textAlign: 'center',
																marginBottom: '1px',
																marginTop: '1px'
															}}
															value={product.sku != null ? product.sku : product.id.toString()}
														/>
													) : null}
													<p
														className={
															(cn('text-[11px] leading-[11px]'),
															printVersion.value === 'version1' ? '' : 'font-bold')
														}>
														{product.size && (
															<>
																S:&nbsp;<span>{product.size}</span>&nbsp;&mdash;&nbsp;
															</>
														)}
														{product.booking?.booth?.number ? (
															<span>#{product.booking.booth.number}</span>
														) : (
															<span>#{product.booking.reference}</span>
														)}
													</p>
												</div>
											</div>
										))}
								</>
							)
						})}
				</div>
			)}
		</>
	)
}

export default ProductsPage
